import { Skeleton, StatTile } from '@/components/ui';
import type {
  DecisionSource,
  HumanReviewFacets,
  HumanReviewRequestFilter,
} from '@/lib/admin/client';

import { DECISION_SOURCE_OPTIONS } from './humanReviewFacetLabels';
import { humanizeSubjectType } from './subjectType';

const PRIORITIES = ['P0', 'P1', 'P2'] as const;
const ITEM_TYPE_LIMIT = 5;

const ALL_FILTER: HumanReviewRequestFilter = {
  state: 'all',
  subjectType: null,
  priority: null,
  decisionSource: null,
  sort: 'newest',
};

function filterFor(
  patch: Partial<HumanReviewRequestFilter>,
  state: HumanReviewRequestFilter['state'] = 'all'
): HumanReviewRequestFilter {
  return {
    ...ALL_FILTER,
    state,
    sort:
      state === 'pending' || state === 'changes_requested'
        ? 'oldest'
        : 'newest',
    ...patch,
  };
}

export type HumanReviewOverviewProps = {
  facets?: HumanReviewFacets;
  pendingFacets?: HumanReviewFacets;
  isLoading: boolean;
  isError: boolean;
  isDisabled: boolean;
  onFilterChange: (filter: HumanReviewRequestFilter) => void;
};

export function HumanReviewOverview({
  facets,
  pendingFacets,
  isLoading,
  isError,
  isDisabled,
  onFilterChange,
}: HumanReviewOverviewProps) {
  if (isLoading) {
    return (
      <section
        aria-label="Review Overview"
        className="grid gap-4 md:grid-cols-2 xl:grid-cols-5"
      >
        {Array.from({ length: 5 }, (_, index) => (
          <Skeleton key={index} className="h-40 w-full" />
        ))}
      </section>
    );
  }
  if (isError) {
    return (
      <p className="text-destructive text-sm">
        Review Overview could not be loaded.
      </p>
    );
  }
  if (isDisabled || !facets || !pendingFacets) return null;

  const itemTypes = Object.entries(facets.subjectType)
    .sort(
      ([leftType, leftCount], [rightType, rightCount]) =>
        rightCount - leftCount || leftType.localeCompare(rightType)
    )
    .slice(0, ITEM_TYPE_LIMIT);

  return (
    <section aria-label="Review Overview" className="space-y-3">
      <h2 className="text-lg font-semibold">Overview</h2>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <StatTile
          title="Queue depth"
          value={facets.state.pending ?? 0}
          description="Pending review items"
          selectAriaLabel="Show pending review items"
          onSelect={() => onFilterChange(filterFor({}, 'pending'))}
        />
        <StatTile
          title="Decision mix"
          value={Object.values(facets.decisionSource).reduce(
            (sum, count) => sum + count,
            0
          )}
          segments={DECISION_SOURCE_OPTIONS.map(({ label, value }) => ({
            label,
            value: facets.decisionSource[value] ?? 0,
            ariaLabel: `Show ${label} review items`,
            onSelect: () =>
              onFilterChange(
                filterFor({ decisionSource: value as DecisionSource })
              ),
          }))}
        />
        <StatTile
          title="Priority"
          value={Object.values(facets.priority).reduce(
            (sum, count) => sum + count,
            0
          )}
          segments={PRIORITIES.map((priority) => ({
            label: priority,
            value: facets.priority[priority] ?? 0,
            ariaLabel: `Show ${priority} review items`,
            onSelect: () => onFilterChange(filterFor({ priority })),
          }))}
        />
        <StatTile
          title="Item types"
          value={itemTypes.length}
          segments={itemTypes.map(([subjectType, count]) => {
            const label = humanizeSubjectType(subjectType);
            return {
              label,
              value: count,
              ariaLabel: `Show ${label} review items`,
              onSelect: () => onFilterChange(filterFor({ subjectType })),
            };
          })}
        />
        <StatTile
          title="P0 pending"
          value={pendingFacets.priority.P0 ?? 0}
          description="Highest-priority pending items"
          selectAriaLabel="Show P0 pending review items"
          onSelect={() =>
            onFilterChange(filterFor({ priority: 'P0' }, 'pending'))
          }
        />
      </div>
    </section>
  );
}
